import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import {
  Mail, RefreshCw, Trash2, ChevronDown, ChevronUp,
  Package, FileText, AlertCircle, Clock, CheckCircle2, Loader2, Inbox, Eye, RotateCcw, Upload, Bug, X, UserCog,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import ParsedOrderViewModal from './ParsedOrderViewModal';

const enconOrange = '#ee7203';

const TYPE_CONFIG = {
  order:                 { label: 'SALES ORDER',    bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  quote:                 { label: 'QUOTE',          bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  purchase_order:        { label: 'PURCHASE ORDER', bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200'  },
  purchase_order_direct: { label: 'DIRECT TO SITE', bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200'    },
  direct_ack:            { label: 'DIRECT ACK',     bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-200'   },
  unknown:               { label: '?',              bg: 'bg-gray-100',   text: 'text-gray-500',   border: 'border-gray-200'   },
};

const STATUS_CONFIG = {
  received: { icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, label: 'Parsing…',  color: 'text-blue-500'  },
  parsed:   { icon: <CheckCircle2 className="w-3.5 h-3.5" />,         label: 'Parsed',    color: 'text-green-600' },
  failed:   { icon: <AlertCircle className="w-3.5 h-3.5" />,          label: 'Failed',    color: 'text-red-500'   },
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function EmailRow({ email, onDelete, onViewOrder, onReparse, isSuperAdmin, assignableUsers, onReassign }) {
  const [expanded, setExpanded] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const tc = TYPE_CONFIG[email.email_type] || TYPE_CONFIG.unknown;
  const sc = STATUS_CONFIG[email.status] || STATUS_CONFIG.received;
  const hasLines = (email.product_lines || []).length > 0;
  const canExpand = hasLines || email.contact_email || email.parse_error || email.goods_in_branch_note;
  const isParsed = email.status === 'parsed';
  const canReassign = isSuperAdmin && ['quote', 'order'].includes(email.email_type);

  const handleReassignChange = async (value) => {
    setReassigning(true);
    try {
      await onReassign(email.id, value === '__unassign__' ? null : value);
    } finally {
      setReassigning(false);
    }
  };

  // For PO types, show the supplier name (who Encon is buying FROM), not "customer"
  const isPOType = email.email_type === 'purchase_order' || email.email_type === 'purchase_order_direct';
  const displayName = isPOType
    ? (email.supplier_company || email.customer_name)
    : email.customer_name;

  return (
    <div className={`rounded-xl border bg-white mb-2 overflow-hidden transition-shadow hover:shadow-sm ${tc.border}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg flex-shrink-0 ${tc.bg} ${tc.text}`}>
          {tc.label}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900 truncate">
              {displayName || <span className="text-gray-400 font-normal">{isPOType ? 'Unknown Supplier' : 'Unknown customer'}</span>}
            </span>
            {email.order_number && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-mono">{email.order_number}</span>
            )}
            {email.reference && (
              <span className="text-xs text-gray-400">Ref: {email.reference}</span>
            )}
            {email.assigned_to_name && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid={`reassigned-badge-${email.id}`}>
                Reassigned to {email.assigned_to_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400 truncate">{email.from_email}</span>
            <span className="text-gray-200">·</span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />{timeAgo(email.received_at)}
            </span>
            <span className="text-gray-200">·</span>
            <span className="text-xs text-gray-400 truncate">{email.filename}</span>
          </div>
        </div>

        <div className={`flex items-center gap-1 text-xs font-medium flex-shrink-0 ${sc.color}`}>
          {sc.icon}<span>{sc.label}</span>
        </div>

        {hasLines && (
          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full flex-shrink-0">
            {email.product_lines.length} line{email.product_lines.length !== 1 ? 's' : ''}
          </span>
        )}

        {email.email_type === 'purchase_order' && email.goods_in_created && (
          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1 border font-semibold ${
            email.goods_in_updated
              ? 'text-blue-700 bg-blue-50 border-blue-200'
              : 'text-green-700 bg-green-50 border-green-200'
          }`}>
            <Package className="w-3 h-3" />
            {email.goods_in_updated ? 'Goods In Updated' : 'Goods In'}
          </span>
        )}

        {canReassign && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Select
              value={email.assigned_to_user_id || '__unassign__'}
              onValueChange={handleReassignChange}
              disabled={reassigning}
            >
              <SelectTrigger
                className="h-8 w-40 text-xs border-purple-200 text-purple-700"
                data-testid={`reassign-select-${email.id}`}
              >
                {reassigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCog className="w-3 h-3 mr-1 flex-shrink-0" />}
                <SelectValue placeholder="Reassign to…" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="__unassign__">— Original sender —</SelectItem>
                {assignableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id} data-testid={`reassign-option-${email.id}-${u.id}`}>
                    {u.full_name || u.username || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isParsed && (
          <button
            onClick={() => onViewOrder(email)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all hover:shadow-sm flex-shrink-0"
            style={{ borderColor: '#ee7203', color: '#ee7203', background: '#fff3e0' }}
            title="View full parsed order details"
            data-testid={`view-order-${email.id}`}
          >
            <Eye className="w-3.5 h-3.5" />
            View
          </button>
        )}

        {isSuperAdmin && (
          <button
            onClick={() => onReparse(email.id)}
            className="p-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-500 text-gray-300 flex-shrink-0 transition-colors"
            title="Re-parse this email (re-run Goods In detection)"
            data-testid={`reparse-email-${email.id}`}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}

        {canExpand && (
          <button onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}

        <button
          onClick={() => onDelete(email.id)}
          className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-300 flex-shrink-0 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
          {email.goods_in_branch_note && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
              <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">{email.goods_in_branch_note}</p>
            </div>
          )}
          {email.contact_email && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Email</p>
              <p className="text-sm text-gray-700">{email.contact_email}</p>
            </div>
          )}
          {email.delivery_postcode && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Delivery Postcode</p>
              <p className="text-sm font-bold text-gray-700">{email.delivery_postcode}</p>
            </div>
          )}
          {email.parse_error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{email.parse_error}</p>
            </div>
          )}
          {hasLines && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Product Lines ({email.product_lines.length})
              </p>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100 text-gray-500 uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-semibold">Code</th>
                      <th className="px-3 py-2 text-left font-semibold">Description</th>
                      <th className="px-3 py-2 text-right font-semibold">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {email.product_lines.map((line, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-gray-700">{line.product_code || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{line.description || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-700 font-medium">{line.qty ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AutomationsPanel({ user }) {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [debugging, setDebugging] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [viewingOrder, setViewingOrder] = useState(null);
  const [freeStockReports, setFreeStockReports] = useState([]);
  const [freeStockLoading, setFreeStockLoading] = useState(false);
  const [dailyOrderEntryReports, setDailyOrderEntryReports] = useState([]);
  const [dailyOrderEntryLoading, setDailyOrderEntryLoading] = useState(false);
  const [orderBookReports, setOrderBookReports] = useState([]);
  const [orderBookLoading, setOrderBookLoading] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';
  const [assignableUsers, setAssignableUsers] = useState([]);
  const fileInputRef = useRef(null);
  const debugInputRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await api.get(`${API}/automations/emails?limit=50`);
      setEmails(res.data?.emails || []);
    } catch {
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadFreeStock = useCallback(async () => {
    setFreeStockLoading(true);
    try {
      const res = await api.get(`${API}/stock-management/free-stock-report/recent`);
      setFreeStockReports(res.data?.reports || []);
    } catch { /* silent */ }
    finally { setFreeStockLoading(false); }
  }, []);

  const loadDailyOrderEntry = useCallback(async () => {
    setDailyOrderEntryLoading(true);
    try {
      const res = await api.get(`${API}/order-book-reports/daily-order-entry?limit=20`);
      setDailyOrderEntryReports(res.data?.reports || []);
    } catch { /* silent */ }
    finally { setDailyOrderEntryLoading(false); }
  }, []);

  const loadOrderBook = useCallback(async () => {
    setOrderBookLoading(true);
    try {
      const res = await api.get(`${API}/order-book-reports/order-book-summary?limit=20`);
      setOrderBookReports(res.data?.reports || []);
    } catch { /* silent */ }
    finally { setOrderBookLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadFreeStock();
    loadDailyOrderEntry();
    loadOrderBook();
  }, [load, loadFreeStock, loadDailyOrderEntry, loadOrderBook]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get(`${API}/users`)
      .then(res => setAssignableUsers((res.data || []).filter(u => u.approved !== false)))
      .catch(() => setAssignableUsers([]));
  }, [isSuperAdmin]);

  const handleReassign = async (emailId, targetUserId) => {
    try {
      const res = await api.put(`${API}/automations/emails/${emailId}/reassign`, { user_id: targetUserId });
      setEmails(prev => prev.map(e => e.id === emailId ? {
        ...e,
        assigned_to_user_id: targetUserId || undefined,
        assigned_to_name: targetUserId ? res.data?.assigned_to_name : undefined,
      } : e));
      toast.success(targetUserId ? `Reassigned to ${res.data?.assigned_to_name}` : 'Reverted to original sender');
    } catch {
      toast.error('Failed to reassign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this email record?')) return;
    try {
      await api.delete(`${API}/automations/emails/${id}`);
      setEmails(prev => prev.filter(e => e.id !== id));
      toast.success('Removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleReparse = async (id) => {
    try {
      const res = await api.post(`${API}/automations/reparse/${id}`);
      const r = res.data;
      if (r.status === 'reparsed') {
        toast.success(
          r.goods_in_updated ? 'Re-parsed — Goods In entry updated!' :
          r.goods_in_created ? 'Re-parsed — Goods In entry created!' :
          'Re-parsed successfully'
        );
      } else if (r.status === 'skipped') {
        toast.info('No stored text — email cannot be re-parsed (too old)');
      } else {
        toast.error(`Re-parse failed: ${r.reason || 'unknown error'}`);
      }
      load(true);
    } catch {
      toast.error('Re-parse request failed');
    }
  };

  const handleReparseAll = async () => {
    if (!window.confirm('Re-parse ALL emails in the system? This will re-run Goods In detection on all stored emails and may use AI credits.')) return;
    setReparsing(true);
    try {
      const res = await api.post(`${API}/automations/reparse-all`);
      const r = res.data;
      toast.success(`Re-parsed ${r.reparsed} emails · ${r.skipped} skipped · ${r.errors} errors`);
      load(true);
    } catch {
      toast.error('Reparse all failed');
    } finally {
      setReparsing(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`${API}/automations/upload-pdf`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${file.name}" uploaded — parsing in progress…`);
      // Auto-refresh after a short delay to let parsing start
      setTimeout(() => load(true), 1500);
      setTimeout(() => load(true), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDebugPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setDebugging(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`${API}/automations/debug-pdf`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDebugResult(res.data);
      toast.success('Debug data captured — see panel below');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Debug failed');
    } finally {
      setDebugging(false);
      if (debugInputRef.current) debugInputRef.current.value = '';
    }
  };

  const filtered = emails.filter(e => {
    if (activeTab === 'orders')     return e.email_type === 'order';
    if (activeTab === 'quotes')     return e.email_type === 'quote';
    if (activeTab === 'goods_in')   return e.email_type === 'purchase_order' || e.email_type === 'purchase_order_direct';
    if (activeTab === 'direct_ack') return e.email_type === 'direct_ack';
    return true;
  });

  const counts = {
    all:        emails.length,
    orders:     emails.filter(e => e.email_type === 'order').length,
    quotes:     emails.filter(e => e.email_type === 'quote').length,
    goods_in:   emails.filter(e => e.email_type === 'purchase_order' || e.email_type === 'purchase_order_direct').length,
    direct_ack: emails.filter(e => e.email_type === 'direct_ack').length,
  };

  const TABS = [
    { key: 'all',        label: 'All',         icon: <Inbox className="w-3.5 h-3.5" />    },
    { key: 'orders',     label: 'Orders',      icon: <Package className="w-3.5 h-3.5" />  },
    { key: 'quotes',     label: 'Quotes',      icon: <FileText className="w-3.5 h-3.5" /> },
    { key: 'goods_in',   label: 'Goods In',    icon: <Package className="w-3.5 h-3.5" style={{ color: '#16a34a' }} /> },
    { key: 'direct_ack', label: 'Direct ACK',  icon: <FileText className="w-3.5 h-3.5" style={{ color: '#2563eb' }} /> },
    { key: 'free_stock', label: 'Free Stock Report', icon: <FileText className="w-3.5 h-3.5" style={{ color: '#2563eb' }} /> },
    { key: 'daily_order_entry', label: 'Daily Order Entry Report', icon: <FileText className="w-3.5 h-3.5" style={{ color: '#7c3aed' }} /> },
    { key: 'order_book', label: 'Order Book',  icon: <Package className="w-3.5 h-3.5" style={{ color: '#16a34a' }} /> },
  ];

  const EXTRA_TAB_KEYS = ['free_stock', 'daily_order_entry', 'order_book'];
  const EXTRA_TAB_COLORS = { free_stock: '#2563eb', daily_order_entry: '#7c3aed', order_book: '#16a34a' };
  const EXTRA_TAB_COUNTS = { free_stock: freeStockReports.length, daily_order_entry: dailyOrderEntryReports.length, order_book: orderBookReports.length };

  function reportTimestamp(iso) {
    if (!iso) return { dateStr: '—', timeStr: '', isToday: false };
    const dt = new Date(iso);
    const dateStr = dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const timeStr = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const isToday = dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) === today;
    return { dateStr, timeStr, isToday };
  }

  return (
    <div className="v3-automations space-y-4 w-full">
      {/* Parsed Order View Modal */}
      {viewingOrder && (
        <ParsedOrderViewModal order={viewingOrder} onClose={() => setViewingOrder(null)} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: enconOrange }}>
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Incoming Emails</h3>
            <p className="text-xs text-gray-400">10-day rolling window · auto-parsed via GPT-4 mini</p>
          </div>
        </div>
        <div className="v3-auto-actions flex items-center gap-2 flex-wrap justify-end">
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handlePdfUpload}
            data-testid="pdf-upload-input"
          />
          <input
            ref={debugInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleDebugPdf}
            data-testid="pdf-debug-input"
          />
          {/* Upload PDF button — visible to all users for manual testing */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-pdf-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: uploading ? '#999' : enconOrange }}
            title="Manually upload a PDF for parsing (useful when email routing isn't configured)"
          >
            {uploading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading…</>
              : <><Upload className="w-3.5 h-3.5" />Upload PDF</>
            }
          </button>
          {/* Debug PDF — super_admin only: dumps raw block coordinates */}
          {isSuperAdmin && (
            <button
              onClick={() => debugInputRef.current?.click()}
              disabled={debugging}
              data-testid="debug-pdf-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
              style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
              title="Upload PDF to inspect raw PyMuPDF block coordinates (debug use only)"
            >
              {debugging
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Analysing…</>
                : <><Bug className="w-3.5 h-3.5" />Debug PDF</>
              }
            </button>
          )}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleReparseAll}
              disabled={reparsing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
              title="Re-run Goods In detection on all stored emails"
              data-testid="reparse-all-btn"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${reparsing ? 'animate-spin' : ''}`} />
              {reparsing ? 'Reparsing…' : 'Reparse All'}
            </button>
          )}
          {isSuperAdmin && (
            <button
              onClick={() => navigate('/settings?tab=cloudflare-incoming')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white transition-colors"
              data-testid="cloudflare-setup-btn"
              style={{ backgroundColor: enconOrange }}
            >
              Cloudflare Setup
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1 flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            style={activeTab === tab.key ? { backgroundColor: EXTRA_TAB_COLORS[tab.key] || enconOrange } : {}}
          >
            {tab.icon}
            {tab.label}
            {!EXTRA_TAB_KEYS.includes(tab.key) && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {counts[tab.key]}
              </span>
            )}
            {EXTRA_TAB_KEYS.includes(tab.key) && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {EXTRA_TAB_COUNTS[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Daily Order Entry Report Tab Content */}
      {activeTab === 'daily_order_entry' ? (
        <div className="space-y-3" data-testid="daily-order-entry-tab-content">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">{dailyOrderEntryReports.length} upload{dailyOrderEntryReports.length !== 1 ? 's' : ''}</p>
            <button onClick={loadDailyOrderEntry} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <RefreshCw className={`w-3 h-3 ${dailyOrderEntryLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {dailyOrderEntryLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7c3aed' }} />
            </div>
          ) : dailyOrderEntryReports.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-2 text-gray-200" />
              <p className="text-sm font-medium">No Daily Order Entry Reports yet</p>
              <p className="text-xs mt-1">Send the "Total Sales Order Book" email to orders@c30dashboard.uk, or use Upload Data</p>
            </div>
          ) : (
            <div className="space-y-2">
              {dailyOrderEntryReports.map((r) => {
                const { dateStr, timeStr, isToday } = reportTimestamp(r.uploaded_at);
                return (
                  <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-purple-200 transition-colors" data-testid={`daily-order-entry-row-${r.id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-green-100' : 'bg-purple-50'}`}>
                      <FileText className={`w-4 h-4 ${isToday ? 'text-green-600' : 'text-purple-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{r.filename || 'Daily Order Entry Report'}</p>
                      <p className="text-xs text-gray-400">{dateStr} at {timeStr}{r.from_email ? ` · from ${r.from_email}` : ''} · {r.line_count ?? 0} line{r.line_count === 1 ? '' : 's'}</p>
                    </div>
                    {isToday && (
                      <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Today
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : activeTab === 'order_book' ? (
        <div className="space-y-3" data-testid="order-book-tab-content">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">{orderBookReports.length} upload{orderBookReports.length !== 1 ? 's' : ''}</p>
            <button onClick={loadOrderBook} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <RefreshCw className={`w-3 h-3 ${orderBookLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {orderBookLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#16a34a' }} />
            </div>
          ) : orderBookReports.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-2 text-gray-200" />
              <p className="text-sm font-medium">No Order Book Summary reports yet</p>
              <p className="text-xs mt-1">Send the "Order Book Summary" email to orders@c30dashboard.uk, or use Upload Data</p>
            </div>
          ) : (
            <div className="space-y-2">
              {orderBookReports.map((r) => {
                const { dateStr, timeStr, isToday } = reportTimestamp(r.uploaded_at);
                return (
                  <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-green-200 transition-colors" data-testid={`order-book-row-${r.id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-green-100' : 'bg-green-50'}`}>
                      <Package className={`w-4 h-4 text-green-600`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{r.filename || 'Order Book Summary'}</p>
                      <p className="text-xs text-gray-400">{dateStr} at {timeStr}{r.from_email ? ` · from ${r.from_email}` : ''} · {r.order_count ?? 0} line{r.order_count === 1 ? '' : 's'}</p>
                    </div>
                    {isToday && (
                      <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Today
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      /* Free Stock Report Tab Content */
      activeTab === 'free_stock' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {freeStockReports.length} upload{freeStockReports.length !== 1 ? 's' : ''} in the last 7 days
            </p>
            <button onClick={loadFreeStock} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <RefreshCw className={`w-3 h-3 ${freeStockLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {freeStockLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
            </div>
          ) : freeStockReports.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-2 text-gray-200" />
              <p className="text-sm font-medium">No free stock reports in the last 7 days</p>
              <p className="text-xs mt-1">Upload via Stock Management page or send to orders@c30dashboard.uk</p>
            </div>
          ) : (
            <div className="space-y-2">
              {freeStockReports.map((r, i) => {
                const dt = r.uploaded_at ? new Date(r.uploaded_at) : null;
                const dateStr = dt ? dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
                const timeStr = dt ? dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                const isToday = dt && dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) === today;
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-200 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isToday ? 'bg-green-100' : 'bg-blue-50'}`}>
                      <FileText className={`w-4 h-4 ${isToday ? 'text-green-600' : 'text-blue-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{r.filename || 'Free Stock Report'}</p>
                      <p className="text-xs text-gray-400">{dateStr} at {timeStr}{r.uploaded_by ? ` · ${r.uploaded_by}` : ''}{r.products_count ? ` · ${r.products_count} products` : ''}</p>
                    </div>
                    {isToday && (
                      <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Today
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      /* ── Normal email tabs content ──────────────────────────────── */
      loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: enconOrange }} />
          <p className="text-sm">Loading emails…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
            <Mail className="w-7 h-7 text-gray-300" />
          </div>
          <div>
            <p className="font-semibold text-gray-500 mb-1">No emails yet</p>
            <p className="text-sm text-gray-400">
              {emails.length === 0
                ? 'Once Cloudflare Email Routing is configured, incoming PDFs will appear here automatically.'
                : `No ${activeTab} emails in the last 10 days.`}
            </p>
          </div>
          {emails.length === 0 && user?.role === 'super_admin' && (
            <button
              onClick={() => navigate('/settings?tab=cloudflare-incoming')}
              className="px-4 py-2 rounded-xl text-sm text-white font-medium"
              style={{ backgroundColor: enconOrange }}
            >
              View Setup Guide
            </button>
          )}
        </div>
      ) : (
        <div>
          <p className="text-xs text-gray-400 mb-3">
            {filtered.length} email{filtered.length !== 1 ? 's' : ''} · auto-deleted after 10 days
          </p>
          {filtered.map(email => (
            <EmailRow
              key={email.id}
              email={email}
              onDelete={handleDelete}
              onViewOrder={setViewingOrder}
              onReparse={handleReparse}
              isSuperAdmin={isSuperAdmin}
              assignableUsers={assignableUsers}
              onReassign={handleReassign}
            />
          ))}
        </div>
      )
      )
      )}

      {/* Debug result panel — super_admin only */}
      {debugResult && isSuperAdmin && (
        <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4" data-testid="debug-result-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bug className="w-4 h-4 text-purple-600" />
              <h4 className="font-bold text-purple-800 text-sm">PDF Block Analysis</h4>
              <span className="text-xs text-purple-500 bg-purple-100 px-2 py-0.5 rounded-full">
                {debugResult.total_blocks} blocks · {Math.round(debugResult.page_size?.width)}×{Math.round(debugResult.page_size?.height)}pt
              </span>
            </div>
            <button onClick={() => setDebugResult(null)} className="text-purple-400 hover:text-purple-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Label column analysis — OLD block-based method (for reference) */}
          <div className="text-xs font-semibold text-purple-600 mb-2">Old block-based method (broken for this PDF — shown for reference):</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {['SUPPLIER', 'INVOICE TO', 'DELIVER TO'].map(lbl => {
              const info = debugResult.label_analysis_old_block_method?.[lbl];
              return (
                <div key={lbl} className={`rounded-lg p-3 border text-xs ${info?.found ? 'bg-white border-purple-200' : 'bg-purple-100 border-purple-200 opacity-60'}`}>
                  <div className="font-bold text-purple-700 mb-1">{lbl}</div>
                  {!info?.found ? (
                    <p className="text-purple-400 italic">Not found in PDF</p>
                  ) : (
                    <>
                      <div className="text-gray-600 space-y-0.5">
                        <div>Label pos: x{info.label_x0}–{info.label_x1}, y{info.label_y0}–{info.label_y1}</div>
                        <div>Center X: <span className="font-mono font-bold text-purple-700">{info.label_center_x}</span></div>
                        <div>Col slack: <span className="font-mono font-bold text-orange-600">{info.col_slack}</span></div>
                        <div>X range: [{info.column_x_range?.[0]} → {info.column_x_range?.[1]}]</div>
                        <div>Neighbour dists: {info.neighbour_dists?.join(', ') || '—'}</div>
                      </div>
                      <div className="mt-2 border-t border-purple-100 pt-1">
                        <div className="font-semibold text-gray-500 mb-0.5">Captured blocks ({info.captured_blocks?.length}):</div>
                        {(info.captured_blocks || []).slice(0, 4).map((cb, i) => (
                          <div key={i} className="text-gray-500 truncate">
                            <span className="font-mono text-purple-500">#{cb.block_index}</span> cx={cb.center_x} dist={cb.dist_from_label_cx}: "{cb.text?.substring(0, 50)}"
                          </div>
                        ))}
                        {(info.captured_blocks || []).length > 4 && (
                          <div className="text-gray-400">… +{info.captured_blocks.length - 4} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Extraction result */}
          <div className="rounded-lg bg-white border border-purple-200 p-3 text-xs mb-3">
            <div className="font-bold text-purple-700 mb-2">Extraction Result — NEW word-based algorithm</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
              <div><span className="font-semibold text-gray-500">supplier:</span> {debugResult.extraction_result_as_po_NEW?.supplier || <span className="text-red-400 italic">null</span>}</div>
              <div><span className="font-semibold text-gray-500">deliver_to:</span> {debugResult.extraction_result_as_po_NEW?.deliver_to || <span className="text-red-400 italic">null</span>}</div>
              <div><span className="font-semibold text-gray-500">invoice_to:</span> {debugResult.extraction_result_as_po_NEW?.invoice_to || <span className="text-red-400 italic">null</span>}</div>
              <div><span className="font-semibold text-gray-500">deliver_postcode:</span> {debugResult.extraction_result_as_po_NEW?.deliver_postcode || <span className="text-red-400 italic">null</span>}</div>
            </div>
          </div>

          {/* Word column headers found */}
          {debugResult.word_column_headers_new && (
            <div className="rounded-lg bg-white border border-green-200 p-3 text-xs mb-3">
              <div className="font-bold text-green-700 mb-2">Word-level column headers detected</div>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(debugResult.word_column_headers_new).map(([lbl, info]) => (
                  <div key={lbl} className="bg-green-50 rounded p-2">
                    <div className="font-bold text-green-700">{lbl}</div>
                    <div className="text-gray-600">cx={info.cx} | wy1={info.wy1}</div>
                    <div className="text-gray-500">at x={info.word_x0}, y={info.word_y0}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All blocks table */}
          <details className="text-xs">
            <summary className="cursor-pointer font-semibold text-purple-700 mb-2">All {debugResult.total_blocks} blocks (click to expand)</summary>
            <div className="overflow-x-auto rounded border border-purple-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-purple-600">#</th>
                    <th className="px-2 py-1 text-left text-purple-600">x0</th>
                    <th className="px-2 py-1 text-left text-purple-600">y0</th>
                    <th className="px-2 py-1 text-left text-purple-600">x1</th>
                    <th className="px-2 py-1 text-left text-purple-600">cx</th>
                    <th className="px-2 py-1 text-left text-purple-600">label?</th>
                    <th className="px-2 py-1 text-left text-purple-600">text</th>
                  </tr>
                </thead>
                <tbody>
                  {(debugResult.all_blocks || []).map(b => (
                    <tr key={b.block_index} className={`border-t border-purple-50 ${b.is_label ? 'bg-yellow-50 font-semibold' : ''}`}>
                      <td className="px-2 py-0.5 font-mono text-gray-400">{b.block_index}</td>
                      <td className="px-2 py-0.5 font-mono">{b.x0}</td>
                      <td className="px-2 py-0.5 font-mono">{b.y0}</td>
                      <td className="px-2 py-0.5 font-mono">{b.x1}</td>
                      <td className="px-2 py-0.5 font-mono font-bold text-purple-600">{b.center_x}</td>
                      <td className="px-2 py-0.5 text-orange-600">{b.is_label || ''}</td>
                      <td className="px-2 py-0.5 text-gray-600 max-w-xs truncate">{b.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
